'use client'

import React, { useState } from 'react'
import { formatFontFamily } from '@/lib/themeHelpers'
import { useScrollAnimation, scrollAnimationClasses } from '@/lib/useScrollAnimation'

interface ContactFAQSectionProps {
  surface: "dark" | "light";
  theme: any;
  radius: number;
  fontFamily: string;
  iconWeight: string;
  gradStart: string;
  gradEnd: string;
  angle: number;
  content: any;
}

export default function ContactFAQSection({
  surface,
  theme,
  radius,
  fontFamily,
  gradStart,
  gradEnd,
  angle,
  content
}: ContactFAQSectionProps) {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const { ref: formRef, isVisible: formVisible } = useScrollAnimation();
  const { ref: faqRef, isVisible: faqVisible } = useScrollAnimation({ rootMargin: '0px 0px -50px 0px' });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    budgetRange: '',
    campaignGoals: '',
    targetLaunchDate: '',
    message: '',
    publication_id: 'https://www.wrll1450.com/'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const backgroundStyle = {
    backgroundImage: surface === "dark" 
      ? `linear-gradient(${angle}deg, ${gradStart}, ${gradEnd})`
      : `linear-gradient(${angle}deg, #f8f9fa, #e9ecef)`,
    fontFamily: formatFontFamily(fontFamily)
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('https://d1lo84safctgv8.cloudfront.net/storefront-lead', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ 
          name: '', 
          email: '', 
          company: '', 
          phone: '', 
          budgetRange: '', 
          campaignGoals: '', 
          targetLaunchDate: '', 
          message: '',
          publication_id: 'https://www.wrll1450.com/'
        });
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section id="contactfaq" className="relative w-full" style={backgroundStyle}>
      <div className="mx-auto max-w-6xl px-6 py-24 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          
          {/* Contact Form */}
          <div 
            ref={formRef as React.RefObject<HTMLDivElement>}
            className={`${scrollAnimationClasses.transition} ${formVisible ? scrollAnimationClasses.visible : scrollAnimationClasses.hidden}`}
          >
            <div className="mb-8">
              <h2 className="text-4xl font-bold mb-4 font-primary" style={{ color: theme.text }}>
                {content.contactTitle}
              </h2>
              <p className="text-lg font-primary" style={{ color: theme.mutedText }}>
                {content.contactDescription}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Hidden publication_id field */}
              <input
                type="hidden"
                name="publication_id"
                value={formData.publication_id}
              />
              
              {/* Name and Email Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 font-primary" style={{ color: theme.text }}>
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 text-sm transition-colors duration-200 focus:outline-none focus:ring-2 font-primary"
                    style={{
                      backgroundColor: theme.card,
                      border: `1px solid ${theme.border}`,
                      borderRadius: radius,
                      color: theme.text
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 font-primary" style={{ color: theme.text }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 text-sm transition-colors duration-200 focus:outline-none focus:ring-2 font-primary"
                    style={{
                      backgroundColor: theme.card,
                      border: `1px solid ${theme.border}`,
                      borderRadius: radius,
                      color: theme.text
                    }}
                  />
                </div>
              </div>

              {/* Company and Phone Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 font-primary" style={{ color: theme.text }}>
                    Company
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 text-sm transition-colors duration-200 focus:outline-none focus:ring-2 font-primary"
                    style={{
                      backgroundColor: theme.card,
                      border: `1px solid ${theme.border}`,
                      borderRadius: radius,
                      color: theme.text
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 font-primary" style={{ color: theme.text }}>
                    Phone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 text-sm transition-colors duration-200 focus:outline-none focus:ring-2 font-primary"
                    style={{
                      backgroundColor: theme.card,
                      border: `1px solid ${theme.border}`,
                      borderRadius: radius,
                      color: theme.text
                    }}
                  />
                </div>
              </div>

              {/* Budget Range */}
              <div>
                <label className="block text-sm font-medium mb-2 font-secondary" style={{ color: theme.text }}>
                  Budget Range
                </label>
                <select
                  name="budgetRange"
                  value={formData.budgetRange}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 text-sm transition-colors duration-200 focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: theme.card,
                    border: `1px solid ${theme.border}`,
                    borderRadius: radius,
                    color: theme.text
                  }}
                >
                  <option value="">Select budget range</option>
                  <option value="under-5k">Under $5K</option>
                  <option value="5k-15k">$5K - $15K</option>
                  <option value="15k-50k">$15K - $50K</option>
                  <option value="50k-plus">$50K+</option>
                </select>
              </div>

              {/* Campaign Goals */}
              <div>
                <label className="block text-sm font-medium mb-2 font-secondary" style={{ color: theme.text }}>
                  Campaign Goals
                </label>
                <select
                  name="campaignGoals"
                  value={formData.campaignGoals}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 text-sm transition-colors duration-200 focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: theme.card,
                    border: `1px solid ${theme.border}`,
                    borderRadius: radius,
                    color: theme.text
                  }}
                >
                  <option value="">Select campaign goal</option>
                  <option value="brand-awareness">Brand Awareness</option>
                  <option value="lead-generation">Lead Generation</option>
                  <option value="event-promotion">Event Promotion</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Target Launch Date */}
              <div>
                <label className="block text-sm font-medium mb-2 font-secondary" style={{ color: theme.text }}>
                  Target Launch Date
                </label>
                <input
                  type="date"
                  name="targetLaunchDate"
                  value={formData.targetLaunchDate}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 text-sm transition-colors duration-200 focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: theme.card,
                    border: `1px solid ${theme.border}`,
                    borderRadius: radius,
                    color: theme.text
                  }}
                />
              </div>
              
              {/* Message */}
              <div>
                <label className="block text-sm font-medium mb-2 font-secondary" style={{ color: theme.text }}>
                  Message *
                </label>
                <textarea
                  name="message"
                  required
                  rows={4}
                  value={formData.message}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 text-sm transition-colors duration-200 focus:outline-none focus:ring-2 resize-vertical"
                  style={{
                    backgroundColor: theme.card,
                    border: `1px solid ${theme.border}`,
                    borderRadius: radius,
                    color: theme.text
                  }}
                  placeholder="Tell us about your advertising goals and how we can help..."
                />
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-8 py-3 text-sm font-semibold shadow-lg transition-all duration-200 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed font-primary"
                style={{
                  backgroundColor: theme.button,
                  color: theme.onButton,
                  borderRadius: radius
                }}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="mt-4 p-4 rounded-lg" style={{
                  backgroundColor: surface === 'dark' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(34, 197, 94, 0.1)',
                  border: '1px solid rgba(34, 197, 94, 0.3)',
                  borderRadius: radius
                }}>
                  <p className="text-sm font-medium font-secondary" style={{ color: '#22c55e' }}>
                    ✓ Thank you for your submission! We will get back to you soon.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mt-4 p-4 rounded-lg" style={{
                  backgroundColor: surface === 'dark' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: radius
                }}>
                  <p className="text-sm font-medium font-secondary" style={{ color: '#ef4444' }}>
                    ✗ There was an error sending your message. Please try again or contact us directly.
                  </p>
                </div>
              )}
            </form>

            {/* Contact Info */}
            <div className="mt-8 pt-8" style={{ borderTop: `1px solid ${theme.border}` }}>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5" fill="currentColor" style={{ color: theme.accent }} viewBox="0 0 20 20">
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                  </svg>
                  <span style={{ color: theme.text }}>(773) 247-6200</span>
                </div>
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5" fill="currentColor" style={{ color: theme.accent }} viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                  <span style={{ color: theme.text }}>{content.contactInfo.email}</span>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div 
            ref={faqRef as React.RefObject<HTMLDivElement>}
            className={`${scrollAnimationClasses.transition} ${faqVisible ? scrollAnimationClasses.visible : scrollAnimationClasses.hidden}`}
            style={{ transitionDelay: '200ms' }}
          >
            <div className="mb-8">
              <h2 className="text-4xl font-bold mb-4 font-primary" style={{ color: theme.text }}>
                {content.faqTitle}
              </h2>
            </div>

            <div className="space-y-4">
              {content.faqItems.map((faq: any, index: number) => (
                <div
                  key={index}
                  className="border-b pb-4"
                  style={{ borderColor: theme.border }}
                >
                  <button
                    onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                    className="w-full text-left flex justify-between items-start gap-4 py-2"
                  >
                    <span className="font-medium text-lg font-primary" style={{ color: theme.text }}>
                      {faq.question}
                    </span>
                    <svg
                      className={`w-5 h-5 transition-transform duration-200 ${openFAQ === index ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      style={{ color: theme.accent }}
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {openFAQ === index && (
                    <div className="mt-3">
                      <p className="font-primary" style={{ color: theme.mutedText }}>
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {/* Need immediate assistance section */}
            <div className="mt-8 p-6" style={{
              backgroundColor: theme.surface === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
              border: `1px solid ${theme.border}`,
              borderRadius: radius
            }}>
              <h3 className="text-lg mb-4 font-primary" style={{ color: theme.text }}>
                Need immediate assistance?
              </h3>
              <p className="text-sm mb-4 font-primary" style={{ color: theme.mutedText }}>
                Contact our advertising team directly:
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 256 256" style={{ color: theme.accent }}>
                    <rect width="256" height="256" fill="none"></rect>
                    <path d="M92.5,124.8a83.6,83.6,0,0,0,39,38.9,8,8,0,0,0,7.9-.6l25-16.7a7.9,7.9,0,0,1,7.6-.7l46.8,20.1a7.9,7.9,0,0,1,4.8,8.3A48,48,0,0,1,176,216,136,136,0,0,1,40,80,48,48,0,0,1,81.9,32.4a7.9,7.9,0,0,1,8.3,4.8l20.1,46.9a8,8,0,0,1-.6,7.5L93,117A8,8,0,0,0,92.5,124.8Z" opacity="0.2"></path>
                    <path d="M92.5,124.8a83.6,83.6,0,0,0,39,38.9,8,8,0,0,0,7.9-.6l25-16.7a7.9,7.9,0,0,1,7.6-.7l46.8,20.1a7.9,7.9,0,0,1,4.8,8.3A48,48,0,0,1,176,216,136,136,0,0,1,40,80,48,48,0,0,1,81.9,32.4a7.9,7.9,0,0,1,8.3,4.8l20.1,46.9a8,8,0,0,1-.6,7.5L93,117A8,8,0,0,0,92.5,124.8Z" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="16"></path>
                  </svg>
                  <span className="text-sm font-medium font-primary" style={{ color: theme.text }}>(773) 247-6200</span>
                </div>
                <div className="flex items-center gap-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 256 256" style={{ color: theme.accent }}>
                    <rect width="256" height="256" fill="none"></rect>
                    <polygon points="224 56 128 144 32 56 224 56" opacity="0.2"></polygon>
                    <polyline points="224 56 128 144 32 56" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="16"></polyline>
                    <path d="M32,56H224a0,0,0,0,1,0,0V192a8,8,0,0,1-8,8H40a8,8,0,0,1-8-8V56A0,0,0,0,1,32,56Z" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="16"></path>
                    <line x1="110.5" y1="128" x2="34.5" y2="197.7" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="16"></line>
                    <line x1="221.5" y1="197.7" x2="145.5" y2="128" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="16"></line>
                  </svg>
                  <span className="text-sm font-medium font-primary" style={{ color: theme.text }}>{content.contactInfo.email}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
